import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ErrorResponse, PaginatedResponse } from '../../api';
import { blockMember, Member, unblockMember } from '../../profile';
import { MemberInfo } from '../../profile/model/MemberInfo';
import { deleteFeaturedStory, Story, updateFeaturedStory } from '../../stories';
import {
  addMemberToPrivateRoom,
  createPrivateRoom,
  createShimMember,
  createSubgroup,
  fetchContentForCarousel,
  fetchCurrentMemberInRoom,
  fetchFeaturedContent,
  fetchFeaturedStory,
  fetchMemberRooms,
  fetchModeratedRooms,
  fetchModeratorsInTheRoom,
  fetchModeratorsInTheRoomCount,
  fetchModeratorSubgroups,
  fetchMyRoomSubgroups,
  fetchOtherSubgroups,
  fetchPeopleInTheRoom,
  fetchPeopleInTheRoomCount,
  fetchPrivateRoomCode,
  fetchRoom,
  fetchRoomsByTopic,
  fetchSuggestedRooms,
  findLinkedRooms,
  findRooms,
  joinRoom,
  joinSubgroup,
  leaveRoom,
  submitConsentToJoinRoom,
} from './actions';
import { Room } from './models';
import { LinkedRoom } from './models/LinkedRoom';

export interface RoomState {
  room?: Room;
  memberRooms: Room[];
  memberRoomsRequestId?: string;
  moderatedRooms: Room[];
  roomsByTopic: {
    [topic: string]: PaginatedResponse<Room[]>;
  };
  roomsSearchResult: Room[];
  suggestedRooms: PaginatedResponse<Room[]>;
  peopleInTheRoom: PaginatedResponse<MemberInfo[]>;
  peopleInTheRoomCount: number;
  isRoomsListView: boolean;
  loadingRoomsByTopic: {
    [topic: string]: boolean;
  };
  loadingRoom: boolean;
  loadingMemberRooms: boolean;
  loadingSearchRooms: boolean;
  loadingJoinRoom: boolean;
  loadingLeaveRoom: boolean;
  loadingSuggestedRooms: boolean;
  loadingPeopleInTheRoom: boolean;
  loadingPeopleInTheRoomCount: boolean;
  loadingPeopleInTheRoomWidget: boolean;
  loadingPrivateRoomCode: boolean;
  linkedRooms: {
    data: LinkedRoom[];
    xMsContinuationToken?: string;
    isLoading: boolean;
  };
  roomError?: ErrorResponse;
  roomMemberError?: ErrorResponse;
  suggestedRoomsError?: ErrorResponse;
  roomsToShareTo?: Room[];
  previousStoryDestination?: { roomId: string; postId: string };
  moderatorsInTheRoom: PaginatedResponse<MemberInfo[]>;
  loadingModeratorsInTheRoom: boolean;
  moderatorsInTheRoomCount: number;
  selectedContentItem?: Story;
  content: Story[];
  featuredContent: {
    data: Story[];
    isLoading: boolean;
  };
  loadingContent: boolean;
  loadingCurrentMemberInRoom: boolean;
  currentMemberInRoom?: Member;
  /** Room the current member-in-room record was fetched for. */
  currentMemberInRoomId?: string;
  loadingSharePrivateRoomCode: boolean;
  sharePrivateRoomCode?: string;
  sharePrivateRoomCodeError?: string;
  privateRoomCodeRequestId?: string;
  myRoomSubgroups: Room[];
  loadingMyRoomSubgroups: boolean;
  otherRoomSubgroups: Room[];
  loadingOtherRoomSubgroups: boolean;
  moderatorRoomSubgroups: Room[];
  loadingModeratorRoomSubgroups: boolean;
  loadingCreateSubgroup: boolean;
  loadingSubmitConsentToJoinRoom: boolean;
}

const initialState: RoomState = {
  room: undefined,
  memberRooms: [],
  roomsByTopic: {},
  roomsSearchResult: [],
  suggestedRooms: { data: [] },
  peopleInTheRoom: { data: [] },
  peopleInTheRoomCount: 0,
  isRoomsListView: false,
  loadingRoom: false,
  loadingMemberRooms: true,
  loadingRoomsByTopic: {},
  loadingSearchRooms: false,
  loadingJoinRoom: false,
  loadingLeaveRoom: false,
  loadingSuggestedRooms: false,
  loadingPeopleInTheRoom: false,
  loadingPeopleInTheRoomCount: false,
  loadingPeopleInTheRoomWidget: false,
  loadingPrivateRoomCode: false,
  linkedRooms: { data: [], xMsContinuationToken: '', isLoading: false },
  roomError: undefined,
  roomMemberError: undefined,
  roomsToShareTo: [],
  previousStoryDestination: undefined,
  moderatorsInTheRoom: { data: [] },
  loadingModeratorsInTheRoom: false,
  moderatorsInTheRoomCount: 0,
  selectedContentItem: undefined,
  content: [],
  featuredContent: {
    data: [],
    isLoading: false,
  },
  loadingContent: false,
  loadingCurrentMemberInRoom: false,
  currentMemberInRoom: undefined,
  moderatedRooms: [],
  loadingSharePrivateRoomCode: false,
  sharePrivateRoomCode: undefined,
  myRoomSubgroups: [],
  loadingMyRoomSubgroups: false,
  otherRoomSubgroups: [],
  loadingOtherRoomSubgroups: false,
  moderatorRoomSubgroups: [],
  loadingModeratorRoomSubgroups: false,
  loadingCreateSubgroup: false,
  loadingSubmitConsentToJoinRoom: false,
};

const roomSlice = createSlice({
  name: 'rooms',
  initialState,
  reducers: {
    clearRoomSearchResults: (state) => {
      state.roomsSearchResult = [];
    },
    setRoomsToShareTo: (state, action) => {
      state.roomsToShareTo = action.payload;
    },
    setRoomsListView: (state, action) => {
      state.isRoomsListView = action.payload;
    },
    clearSuggestedRooms: (state) => {
      state.suggestedRooms.data = [];
    },
    clearRoom: (state) => {
      state.room = undefined;
      state.roomError = undefined;
    },
    updatePeopleInTheRoomInfo: (state, action: PayloadAction<string>) => {
      state.peopleInTheRoom.data.forEach((people) => {
        if (people.memberId === action.payload) people.isCompanion = false;
      });
    },
    updateAddCompanionPeopleInTheRoomInfo: (
      state,
      action: PayloadAction<string>
    ) => {
      state.peopleInTheRoom.data = [
        ...state.peopleInTheRoom.data.filter(
          (people) => people.memberId !== action.payload
        ),
      ];
    },
    setPreviousStoryDestination: (
      state,
      action: PayloadAction<{ roomId: string; postId: string } | undefined>
    ) => {
      state.previousStoryDestination = action.payload;
    },
    setSelectedContentItem: (
      state,
      action: PayloadAction<Story | undefined>
    ) => {
      state.selectedContentItem = action.payload;
    },
    clearCurrentMemberInRoom: (state) => {
      state.currentMemberInRoom = undefined;
      state.currentMemberInRoomId = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRoom.pending, (state) => {
        state.loadingRoom = true;
        state.roomError = undefined;
      })
      .addCase(fetchRoom.rejected, (state, action) => {
        state.roomError = action.payload;
        state.room = undefined;
        state.loadingRoom = false;
      })
      .addCase(fetchRoom.fulfilled, (state, action) => {
        state.room = action.payload;
        state.loadingRoom = false;
      })

      .addCase(fetchMyRoomSubgroups.pending, (state) => {
        state.loadingMyRoomSubgroups = true;
      })
      .addCase(fetchMyRoomSubgroups.rejected, (state, action) => {
        state.loadingMyRoomSubgroups = false;
        state.myRoomSubgroups = [];
      })
      .addCase(fetchMyRoomSubgroups.fulfilled, (state, action) => {
        state.loadingMyRoomSubgroups = false;
        state.myRoomSubgroups = action.payload;
      })

      .addCase(fetchOtherSubgroups.pending, (state) => {
        state.loadingOtherRoomSubgroups = true;
      })
      .addCase(fetchOtherSubgroups.rejected, (state, action) => {
        state.loadingOtherRoomSubgroups = false;
        state.otherRoomSubgroups = [];
      })
      .addCase(fetchOtherSubgroups.fulfilled, (state, action) => {
        state.loadingOtherRoomSubgroups = false;
        state.otherRoomSubgroups = action.payload;
      })

      .addCase(fetchModeratorSubgroups.pending, (state) => {
        state.loadingModeratorRoomSubgroups = true;
      })
      .addCase(fetchModeratorSubgroups.rejected, (state, action) => {
        state.loadingModeratorRoomSubgroups = false;
        state.moderatorRoomSubgroups = [];
      })
      .addCase(fetchModeratorSubgroups.fulfilled, (state, action) => {
        state.loadingModeratorRoomSubgroups = false;
        state.moderatorRoomSubgroups = action.payload;
      })
      .addCase(createSubgroup.pending, (state) => {
        state.loadingCreateSubgroup = true;
      })
      .addCase(createSubgroup.rejected, (state, action) => {
        state.loadingCreateSubgroup = false;
      })
      .addCase(createSubgroup.fulfilled, (state, action) => {
        state.loadingCreateSubgroup = false;
        state.moderatorRoomSubgroups = [
          ...state.moderatorRoomSubgroups,
          action.payload,
        ];
      })
      .addCase(fetchMemberRooms.pending, (state, action) => {
        state.memberRoomsRequestId = action.meta.requestId;
        state.loadingMemberRooms = true;
        state.roomMemberError = undefined;
      })
      .addCase(fetchMemberRooms.rejected, (state, action) => {
        if (state.memberRoomsRequestId !== action.meta.requestId) return;
        state.loadingMemberRooms = false;
        state.roomMemberError = action.payload;
      })
      .addCase(fetchMemberRooms.fulfilled, (state, action) => {
        if (state.memberRoomsRequestId !== action.meta.requestId) return;
        state.memberRooms = action.payload ?? [];
        state.loadingMemberRooms = false;
      })

      .addCase(fetchRoomsByTopic.pending, (state, action) => {
        state.loadingRoomsByTopic[action.meta.arg] = true;
      })
      .addCase(fetchRoomsByTopic.rejected, (state, action) => {
        state.loadingRoomsByTopic[action.meta.arg] = false;
      })
      .addCase(fetchRoomsByTopic.fulfilled, (state, action) => {
        state.roomsByTopic[action.meta.arg] = {
          data: action.payload,
        };
        state.loadingRoomsByTopic[action.meta.arg] = false;
      })

      .addCase(findRooms.pending, (state) => {
        state.loadingSearchRooms = true;
      })
      .addCase(findRooms.fulfilled, (state, action) => {
        state.roomsSearchResult = action.payload;
        state.loadingSearchRooms = false;
      })
      .addCase(findRooms.rejected, (state) => {
        state.roomsSearchResult = [];
        state.loadingSearchRooms = false;
      })

      .addCase(joinRoom.pending, (state) => {
        state.loadingJoinRoom = true;
      })
      .addCase(joinRoom.rejected, (state) => {
        state.loadingJoinRoom = false;
      })
      .addCase(joinRoom.fulfilled, (state, action) => {
        state.loadingJoinRoom = false;
        state.peopleInTheRoomCount++;
      })

      .addCase(joinSubgroup.fulfilled, (state, action) => {
        state.myRoomSubgroups = [...state.myRoomSubgroups, action.payload];
        state.otherRoomSubgroups = state.otherRoomSubgroups.filter(
          (subgroup) => subgroup.id !== action.payload.id
        );
      })

      .addCase(leaveRoom.pending, (state) => {
        state.loadingLeaveRoom = true;
      })
      .addCase(leaveRoom.rejected, (state) => {
        state.loadingLeaveRoom = false;
      })
      .addCase(leaveRoom.fulfilled, (state, action) => {
        state.room = action.payload;
        state.loadingLeaveRoom = false;
      })

      .addCase(fetchSuggestedRooms.pending, (state) => {
        state.loadingSuggestedRooms = true;
      })
      .addCase(fetchSuggestedRooms.rejected, (state, action) => {
        state.loadingSuggestedRooms = false;
        state.suggestedRoomsError = action.payload;
      })
      .addCase(fetchSuggestedRooms.fulfilled, (state, action) => {
        state.loadingSuggestedRooms = false;

        let suggestedRooms = state.suggestedRooms.data || [];
        if (action.meta.arg.xMsContinuationToken) {
          suggestedRooms.push(...action.payload.data);
        } else {
          suggestedRooms = [...action.payload.data];
        }

        state.suggestedRooms = {
          data: suggestedRooms,
          xMsContinuationToken: action.payload?.xMsContinuationToken,
        };
      })

      .addCase(fetchPeopleInTheRoom.pending, (state, action) => {
        state.loadingPeopleInTheRoom = true;
        if (!action.meta.arg.xMsContinuationToken) {
          state.loadingPeopleInTheRoomWidget = true;
        }
      })
      .addCase(fetchPeopleInTheRoom.fulfilled, (state, action) => {
        let peopleInTheRoom = state.peopleInTheRoom.data || [];
        if (action.meta.arg.xMsContinuationToken) {
          peopleInTheRoom?.push(...action.payload?.data);
        } else if (action.payload?.data) {
          peopleInTheRoom = [...action.payload?.data];
        }
        state.peopleInTheRoom = {
          data: peopleInTheRoom,
          xMsContinuationToken: action.payload?.xMsContinuationToken,
        };
        state.loadingPeopleInTheRoom = false;
        state.loadingPeopleInTheRoomWidget = false;
      })
      .addCase(fetchPeopleInTheRoom.rejected, (state) => {
        state.loadingPeopleInTheRoom = false;
        state.loadingPeopleInTheRoomWidget = false;
      })

      .addCase(fetchPeopleInTheRoomCount.fulfilled, (state, action) => {
        state.peopleInTheRoomCount = action.payload;
      })
      .addCase(fetchPeopleInTheRoomCount.pending, (state) => {
        state.loadingPeopleInTheRoomCount = true;
      })
      .addCase(fetchPeopleInTheRoomCount.rejected, (state) => {
        state.loadingPeopleInTheRoomCount = false;
      })
      .addCase(findLinkedRooms.fulfilled, (state, action) => {
        let linkedRooms = state.linkedRooms?.data || [];

        if (action.payload?.data) {
          if (action.meta.arg.xMsContinuationToken && action.payload?.data) {
            linkedRooms?.push(...action.payload?.data);
          } else {
            linkedRooms = [...action.payload?.data];
          }
        }

        state.linkedRooms = {
          data: linkedRooms,
          xMsContinuationToken: action.payload?.xMsContinuationToken,
          isLoading: false,
        };
      })
      .addCase(findLinkedRooms.pending, (state) => {
        state.linkedRooms = {
          ...state?.linkedRooms,
          isLoading: true,
        };
      })
      .addCase(findLinkedRooms.rejected, (state) => {
        state.linkedRooms = {
          data: [],
          xMsContinuationToken: '',
          isLoading: false,
        };
      })
      .addCase(blockMember.fulfilled, (state, action) => {
        state.peopleInTheRoom.data = state.peopleInTheRoom.data.map((member) =>
          member.memberId !== action.meta.arg.memberId
            ? member
            : { ...member, isBlockedMember: true }
        );
      })
      .addCase(unblockMember.fulfilled, (state, action) => {
        state.peopleInTheRoom.data = state.peopleInTheRoom.data.map((member) =>
          member.memberId !== action.meta.arg.memberId
            ? member
            : { ...member, isBlockedMember: false }
        );
      })
      .addCase(addMemberToPrivateRoom.pending, (state) => {
        state.loadingPrivateRoomCode = true;
      })
      .addCase(addMemberToPrivateRoom.fulfilled, (state) => {
        state.loadingPrivateRoomCode = false;
      })
      .addCase(addMemberToPrivateRoom.rejected, (state) => {
        state.loadingPrivateRoomCode = false;
      })
      .addCase(fetchModeratorsInTheRoom.pending, (state) => {
        state.loadingModeratorsInTheRoom = true;
      })
      .addCase(fetchModeratorsInTheRoom.rejected, (state) => {
        state.loadingModeratorsInTheRoom = false;
      })
      .addCase(fetchModeratorsInTheRoom.fulfilled, (state, action) => {
        let moderatorsInTheRoom = state.moderatorsInTheRoom.data || [];
        if (action.meta.arg.xMsContinuationToken) {
          moderatorsInTheRoom?.push(...action.payload?.data);
        } else {
          moderatorsInTheRoom = [...action.payload?.data];
        }
        state.moderatorsInTheRoom = {
          data: moderatorsInTheRoom,
          xMsContinuationToken: action.payload?.xMsContinuationToken,
        };
        state.loadingModeratorsInTheRoom = false;
      })
      .addCase(fetchModeratorsInTheRoomCount.fulfilled, (state, action) => {
        state.moderatorsInTheRoomCount = action.payload;
      })
      .addCase(fetchContentForCarousel.rejected, (state, action) => {
        state.loadingContent = false;
      })
      .addCase(fetchContentForCarousel.pending, (state, action) => {
        state.loadingContent = true;
      })
      .addCase(fetchContentForCarousel.fulfilled, (state, action) => {
        state.content = action.payload;
        state.loadingContent = false;
      })

      .addCase(fetchFeaturedContent.fulfilled, (state, action) => {
        state.featuredContent = {
          data: action.payload,
          isLoading: false,
        };
      })
      .addCase(fetchFeaturedContent.pending, (state) => {
        state.featuredContent = {
          data: [],
          isLoading: true,
        };
      })
      .addCase(fetchFeaturedContent.rejected, (state) => {
        state.featuredContent = {
          data: state.featuredContent.data,
          isLoading: false,
        };
      })

      .addCase(fetchFeaturedStory.fulfilled, (state, action) => {
        state.selectedContentItem = action.payload;

        const stories = [...state.content];
        state.content = stories.map((contentItem) =>
          contentItem.id === action.payload.id ? action.payload : contentItem
        );
      })

      .addCase(deleteFeaturedStory.fulfilled, (state, action) => {
        state.content = state.content.filter(
          (contentItem) => contentItem.id !== action.meta.arg
        );
      })
      .addCase(fetchModeratedRooms.fulfilled, (state, action) => {
        state.moderatedRooms = action.payload;
      })
      .addCase(updateFeaturedStory.fulfilled, (state, action) => {
        state.content = state.content.map((contentItem) =>
          contentItem.id !== action.meta.arg.storyId
            ? contentItem
            : action.payload
        );
      })
      .addCase(createPrivateRoom.fulfilled, (state, action) => {
        state.room = action.payload;
        state.loadingRoom = false;
      })
      .addCase(createPrivateRoom.pending, (state) => {
        state.loadingRoom = true;
      })
      .addCase(createPrivateRoom.rejected, (state) => {
        state.loadingRoom = false;
      })
      .addCase(fetchCurrentMemberInRoom.fulfilled, (state, action) => {
        state.currentMemberInRoom = action.payload;
        state.currentMemberInRoomId = action.meta.arg;
        state.loadingCurrentMemberInRoom = false;
      })
      .addCase(fetchCurrentMemberInRoom.pending, (state) => {
        state.loadingCurrentMemberInRoom = true;
      })
      .addCase(fetchCurrentMemberInRoom.rejected, (state) => {
        state.loadingCurrentMemberInRoom = false;
      })
      .addCase(createShimMember.fulfilled, (state, action) => {
        state.currentMemberInRoom = action.payload;
        state.currentMemberInRoomId = action.meta.arg.roomId;
        state.loadingCurrentMemberInRoom = false;
        if (state.room?.id === action.meta.arg.roomId) state.room.doesMemberHaveShim = true;
      })
      .addCase(createShimMember.pending, (state) => {
        state.loadingCurrentMemberInRoom = true;
      })
      .addCase(createShimMember.rejected, (state, action) => {
        state.loadingCurrentMemberInRoom = false;
        state.roomMemberError = action.payload;
      })
      .addCase(fetchPrivateRoomCode.fulfilled, (state, action) => {
        if (state.privateRoomCodeRequestId !== action.meta.requestId) return;
        state.sharePrivateRoomCode = action.payload;
        state.sharePrivateRoomCodeError = undefined;
        state.loadingSharePrivateRoomCode = false;
      })
      .addCase(fetchPrivateRoomCode.pending, (state, action) => {
        state.privateRoomCodeRequestId = action.meta.requestId;
        state.sharePrivateRoomCode = undefined;
        state.sharePrivateRoomCodeError = undefined;
        state.loadingSharePrivateRoomCode = true;
      })
      .addCase(fetchPrivateRoomCode.rejected, (state, action) => {
        if (state.privateRoomCodeRequestId !== action.meta.requestId) return;
        const data: unknown = action.payload?.data;
        const code = data && typeof data === 'object' && 'error_code' in data
          ? data.error_code : undefined;
        state.sharePrivateRoomCodeError = code === 'ROOM_INVITE_BRANDING_REQUIRED'
          ? 'roomInvite.brandingRequired'
          : code === 'ROOM_INVITE_PROFILE_ONBOARDING_REQUIRED'
          ? 'roomInvite.profileRequired'
          : 'roomInvite.createError';
        state.loadingSharePrivateRoomCode = false;
      })
      .addCase(submitConsentToJoinRoom.fulfilled, (state, action) => {
        state.loadingSubmitConsentToJoinRoom = false;
        if (state.room?.id === action.meta.arg && state.room.consent)
          state.room.consent.memberHasConsented = true;
      })
      .addCase(submitConsentToJoinRoom.pending, (state) => {
        state.loadingSubmitConsentToJoinRoom = true;
      })
      .addCase(submitConsentToJoinRoom.rejected, (state) => {
        state.loadingSubmitConsentToJoinRoom = false;
      });
  },
});

export const {
  clearRoomSearchResults,
  setRoomsListView,
  setRoomsToShareTo,
  clearSuggestedRooms,
  clearRoom,
  updatePeopleInTheRoomInfo,
  updateAddCompanionPeopleInTheRoomInfo,
  setPreviousStoryDestination,
  setSelectedContentItem,
  clearCurrentMemberInRoom,
} = roomSlice.actions;

export default roomSlice.reducer;
